// Country -> State -> City cascading select, backed by the offline
// `country-state-city` dataset (250 countries, no network calls). Cities load on first use
// (utils/geo: 7.7 MB, kept out of the main bundle).
// Added 2026-07-23: Avinash's instruction to turn Current Location and
// Permanent Address from free text into structured, cascading dropdowns
// rather than typed text a candidate could enter inconsistently.
import { useEffect, useMemo, useState } from "react";
import { Country, State, loadCities } from "../../utils/geo";

// value: { countryCode, stateCode, city } -- city is a plain name (city-level
// ISO codes don't exist in this dataset the way country/state ones do).
// onChange receives the next value object.
export default function LocationCascadeSelect({ value, onChange, disabled }) {
  const countryCode = value?.countryCode || "";
  const stateCode = value?.stateCode || "";
  const city = value?.city || "";

  const countries = useMemo(() => Country.getAllCountries(), []);
  const states = useMemo(
    () => (countryCode ? State.getStatesOfCountry(countryCode) : []),
    [countryCode],
  );
  const [cities, setCities] = useState([]);
  const [citiesError, setCitiesError] = useState("");
  useEffect(() => {
    let current = true;
    setCities([]);
    if (!countryCode || !stateCode) return undefined;
    loadCities()
      .then((City) => current && setCities(City.getCitiesOfState(countryCode, stateCode)))
      .catch((err) => {
        console.error("[LocationCascadeSelect] Failed to load cities:", err);
        if (current) setCitiesError("Could not load the city list");
      });
    return () => {
      current = false;
    };
  }, [countryCode, stateCode]);

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <label className="block">
        <div className="mb-1 text-xs font-semibold text-gray-700">
          Country
        </div>
        <select
          value={countryCode}
          disabled={disabled}
          onChange={(e) =>
            onChange({ countryCode: e.target.value, stateCode: "", city: "" })
          }
          className="w-full rounded-xl border bg-white px-3 py-2 text-sm outline-none focus:border-gray-900"
        >
          <option value="">Select country</option>
          {countries.map((c) => (
            <option key={c.isoCode} value={c.isoCode}>
              {c.name}
            </option>
          ))}
        </select>
      </label>

      <label className="block">
        <div className="mb-1 text-xs font-semibold text-gray-700">State</div>
        <select
          value={stateCode}
          disabled={disabled || !countryCode}
          onChange={(e) =>
            onChange({ countryCode, stateCode: e.target.value, city: "" })
          }
          className="w-full rounded-xl border bg-white px-3 py-2 text-sm outline-none focus:border-gray-900 disabled:bg-gray-50 disabled:text-gray-400"
        >
          <option value="">
            {countryCode ? "Select state" : "Select country first"}
          </option>
          {states.map((s) => (
            <option key={s.isoCode} value={s.isoCode}>
              {s.name}
            </option>
          ))}
        </select>
      </label>

      <label className="block">
        <div className="mb-1 text-xs font-semibold text-gray-700">City</div>
        <select
          value={city}
          disabled={disabled || !stateCode}
          onChange={(e) => onChange({ countryCode, stateCode, city: e.target.value })}
          className="w-full rounded-xl border bg-white px-3 py-2 text-sm outline-none focus:border-gray-900 disabled:bg-gray-50 disabled:text-gray-400"
        >
          <option value="">
            {citiesError || (stateCode ? "Select city" : "Select state first")}
          </option>
          {city && !cities.some((c) => c.name === city) && (
            // A stored city the dataset doesn't list (renamed, e.g. Gurugram/Gurgaon) stays selectable
            <option value={city}>{city}</option>
          )}
          {cities.map((c) => (
            <option key={`${c.name}-${c.latitude}`} value={c.name}>
              {c.name}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

// Composes a value object into the single "City, State, Country" string
// the backend's candidateCurrentLocation / permanent_address columns
// (both plain strings) already expect -- no backend schema change needed,
// this only changes how the string gets built on the frontend.
export function formatLocation(value) {
  if (!value?.countryCode) return "";
  const country = Country.getCountryByCode(value.countryCode);
  const state = value.stateCode
    ? State.getStateByCodeAndCountry(value.stateCode, value.countryCode)
    : null;
  return [value.city, state?.name, country?.name].filter(Boolean).join(", ");
}

// Best-effort reverse parse of an existing "City, State, Country" string
// (or legacy free-text values already in the DB) back into a cascade
// value -- used to prefill the dropdowns when editing a candidate who
// already has a value stored the old way. Falls back to all-empty if it
// can't confidently match, rather than guessing wrong.
// Short forms people type for a country ("USA", "UK", ISO codes like "IN")
const COUNTRY_ALIASES = { usa: "US", "u.s.a.": "US", "u.s.": "US", "united states of america": "US", uk: "GB", "great britain": "GB", uae: "AE" };

function findCountry(name) {
  const key = (name || "").trim().toLowerCase();
  if (!key) return null;
  const code = COUNTRY_ALIASES[key] || (key.length === 2 ? key.toUpperCase() : null);
  return Country.getAllCountries().find((c) => c.name.toLowerCase() === key || (code && c.isoCode === code)) || null;
}

export function parseLocation(text) {
  if (!text) return { countryCode: "", stateCode: "", city: "" };
  const parts = text.split(",").map((p) => p.trim()).filter(Boolean);
  const countryName = parts[parts.length - 1];
  const country = findCountry(countryName);
  if (!country) {
    // Legacy "City, State" without a country: infer the country from the
    // state name when that name identifies one state (or one in IN/US,
    // where this platform recruits); otherwise leave it for the user.
    const matches = State.getAllStates().filter(
      (s) => s.name.toLowerCase() === (countryName || "").toLowerCase(),
    );
    const preferred = matches.length === 1
      ? matches[0]
      : matches.find((s) => s.countryCode === "IN") || matches.find((s) => s.countryCode === "US");
    if (!preferred) return { countryCode: "", stateCode: "", city: "" };
    return {
      countryCode: preferred.countryCode,
      stateCode: preferred.isoCode,
      city: parts.length >= 2 ? parts[0] : "",
    };
  }

  const stateName = parts.length >= 2 ? parts[parts.length - 2] : "";
  const state = stateName
    ? State.getStatesOfCountry(country.isoCode).find(
        (s) => s.name.toLowerCase() === stateName.toLowerCase(),
      )
    : null;

  const city = parts.length >= 3 ? parts[0] : "";

  return {
    countryCode: country.isoCode,
    stateCode: state?.isoCode || "",
    city: state ? city : "",
  };
}
